import React, { useState, useEffect } from 'react';
import { _T, C } from '../../../shared/theme';
import { s_PB, s_GB } from '../../../shared/styles';

function ExercisePrep({ exercise, lang, onReady }) {
  const nl = lang === "nl";
  const prep = exercise.prep || [];
  const [step, setStep] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);
  const [dots, setDots] = useState('');

  const current = prep[step];
  const isLast = step >= prep.length - 1;

  // Animated dots for atmosphere
  useEffect(() => {
    const id = setInterval(() => setDots(d => d.length >= 3 ? '' : d + '.'), 600);
    return () => clearInterval(id);
  }, []);

  const advance = () => {
    if (isLast) { onReady(); return; }
    setFadeIn(false);
    setTimeout(() => { setStep(s => s + 1); setFadeIn(true); }, 250);
  };

  if (!prep.length) { onReady(); return null; }

  const progressPct = ((step + 1) / prep.length) * 100;

  return (
    <div style={{
      minHeight: "100vh",
      background: `linear-gradient(160deg, ${exercise.bgColor} 0%, #020d0b 60%)`,
      display: "flex",
      flexDirection: "column",
      position: "relative",
      overflow: "hidden",
    }}>
      {/* Ambient glow */}
      <div style={{
        position: "absolute", top: "20%", left: "50%", transform: "translateX(-50%)",
        width: 300, height: 300, borderRadius: "50%",
        background: `radial-gradient(circle, ${exercise.color}18 0%, transparent 70%)`,
        pointerEvents: "none",
      }}/>

      {/* Header */}
      <div style={{ padding: "24px 20px 0", display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 12,
          background: `${exercise.color}20`, border: `1px solid ${exercise.color}44`,
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20,
        }}>{exercise.icon}</div>
        <div>
          <div style={{ fontSize: 9, color: exercise.color, letterSpacing: 3, fontWeight: 700 }}>
            {nl ? "VOORBEREIDING" : "PREPARATION"}
          </div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: 15, color: C.white }}>
            {nl ? exercise.label : exercise.labelEN}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ margin: "20px 20px 0", height: 2, background: `${exercise.color}20`, borderRadius: 2 }}>
        <div style={{
          height: "100%", borderRadius: 2,
          background: exercise.color,
          width: `${progressPct}%`,
          transition: "width 0.4s ease",
        }}/>
      </div>

      {/* Step counter */}
      <div style={{ textAlign: "center", marginTop: 10, color: `${exercise.color}80`, fontSize: 11, letterSpacing: 2 }}>
        {step + 1} / {prep.length}
      </div>

      {/* Main content */}
      <div style={{
        flex: 1, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        padding: "20px 32px",
        opacity: fadeIn ? 1 : 0,
        transform: fadeIn ? "translateY(0)" : "translateY(8px)",
        transition: "opacity 0.3s ease, transform 0.3s ease",
      }}>
        {/* Big icon */}
        <div style={{
          fontSize: 64, marginBottom: 28, lineHeight: 1,
          filter: `drop-shadow(0 0 24px ${exercise.color}60)`,
        }}>
          {current?.icon}
        </div>

        {/* Instruction text */}
        <div style={{
          fontFamily: "Georgia,serif",
          fontSize: 20,
          color: C.white,
          textAlign: "center",
          lineHeight: 1.65,
          marginBottom: 12,
          maxWidth: 320,
        }}>
          {nl ? current?.text : current?.textEN}
        </div>

        {/* Subtle breathing prompt while reading */}
        <div style={{
          fontSize: 12, color: `${C.silver}60`, marginTop: 8, letterSpacing: 1,
        }}>
          {nl ? `ademhaal${dots}` : `breathe${dots}`}
        </div>
      </div>

      {/* Bottom action */}
      <div style={{ padding: "0 20px 40px" }}>
        <button
          onClick={advance}
          style={s_PB({
            width: "100%", fontSize: 16, padding: "16px",
            borderRadius: 16,
            background: isLast
              ? `linear-gradient(135deg, ${exercise.color}, ${exercise.color}cc)`
              : `${exercise.color}22`,
            border: `1px solid ${exercise.color}${isLast ? 'ff' : '44'}`,
            color: isLast ? "#0a1220" : exercise.color,
            fontWeight: 700,
          })}
        >
          {isLast
            ? (nl ? "▶  Sessie starten" : "▶  Start session")
            : (nl ? "Begrepen →" : "Got it →")
          }
        </button>

        {/* Skip for experienced users */}
        {step === 0 && (
          <button
            onClick={onReady}
            style={{
              display: "block", width: "100%", marginTop: 10,
              background: "none", border: "none",
              color: `${C.silver}50`, fontSize: 12, cursor: "pointer",
              padding: "6px 0",
            }}
          >
            {nl ? "Overslaan — direct starten" : "Skip — start directly"}
          </button>
        )}
      </div>
    </div>
  );
}

export default ExercisePrep;
